import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../models/User';
import type {
  ClientRequest,
  ClientRequestSource,
  ClientRequestStatus,
} from '../models/ClientRequest';
import { newOperationId, supportAction } from '../services/support';

const toDate = (value: unknown): Date => {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
};

const toNullableDate = (value: unknown): Date | null => {
  if (value == null) return null;
  return toDate(value);
};

const toSource = (value: unknown): ClientRequestSource | undefined => {
  if (value === 'portal' || value === 'chatgpt-connector') return value;
  return undefined;
};

/** Keep only string→string pairs; drop empty maps so portal docs stay clean. */
const toIntake = (value: unknown): Record<string, string> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries);
};

export function useClientRequests(clientIdFilter?: string | null) {
  const { currentUser, clientId: authClientId } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const scopedClientId = isSuperAdmin
    ? clientIdFilter ?? null
    : authClientId;

  const [requests, setRequests] = useState<ClientRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!currentUser) {
      setRequests([]);
      setLoading(false);
      return;
    }

    if (!isSuperAdmin && !scopedClientId) {
      setRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let unsubscribe: Unsubscribe | undefined;

    try {
      const base = collection(db, 'clientRequests');
      const q = scopedClientId
        ? query(base, where('clientId', '==', scopedClientId))
        : query(base, orderBy('createdAt', 'desc'));

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const rows = snapshot.docs.map((docSnap) => {
            const data = docSnap.data();
            const source = toSource(data.source);
            const intake = toIntake(data.intake);
            return {
              id: docSnap.id,
              clientId: String(data.clientId || ''),
              projectId: data.projectId ? String(data.projectId) : null,
              title: data.title ? String(data.title) : null,
              description: String(data.description || ''),
              type: data.type === 'problem' ? 'problem' : 'feature',
              status: (['pending', 'approved', 'rejected'].includes(data.status)
                ? data.status
                : 'pending') as ClientRequestStatus,
              createdBy: String(data.createdBy || ''),
              ...(source ? { source } : {}),
              ...(intake ? { intake } : {}),
              platform: data.platform ? String(data.platform) : null,
              createdByName: data.createdByName ? String(data.createdByName) : null,
              clientEndorsement: data.clientEndorsement?.by
                ? {
                    byName: data.clientEndorsement.byName ?? null,
                    note: data.clientEndorsement.note ?? null,
                  }
                : null,
              clientPriority: Number.isFinite(Number(data.clientPriority?.rank))
                ? {
                    rank: Number(data.clientPriority.rank),
                    note: data.clientPriority.note ?? null,
                  }
                : null,
              taskId: data.taskId ? String(data.taskId) : null,
              reviewedBy: data.reviewedBy ? String(data.reviewedBy) : null,
              reviewedAt: toNullableDate(data.reviewedAt),
              reviewNote: data.reviewNote ? String(data.reviewNote) : null,
              createdAt: toDate(data.createdAt),
              updatedAt: toDate(data.updatedAt),
            } satisfies ClientRequest;
          });

          if (scopedClientId) {
            rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          }

          setRequests(rows);
          setLoading(false);
          setError(null);
        },
        (err) => {
          setError(err as Error);
          setLoading(false);
        }
      );
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }

    return () => {
      unsubscribe?.();
    };
  }, [currentUser, isSuperAdmin, scopedClientId]);

  const rejectRequest = useCallback(
    async (requestId: string, reviewNote?: string) => {
      if (!currentUser) throw new Error('Not authenticated');
      const note = reviewNote?.trim();
      await updateDoc(doc(db, 'clientRequests', requestId), {
        status: 'rejected',
        reviewedBy: currentUser.uid,
        reviewedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        ...(note ? { reviewNote: note } : { reviewNote: null }),
      });
    },
    [currentUser]
  );

  const approveRequest = useCallback(
    async (
      request: ClientRequest,
      options?: { projectId?: string | null; reviewNote?: string }
    ) => {
      if (!currentUser) throw new Error('Not authenticated');
      if (request.status !== 'pending') {
        throw new Error('Only pending requests can be approved');
      }

      const projectId = (options?.projectId || request.projectId || '').trim();
      if (!projectId) {
        throw new Error('A project is required to create the task');
      }

      const result = await supportAction({ action: 'approveRequest', requestId: request.id, projectId, ...(options?.reviewNote ? { reviewNote: options.reviewNote } : {}) }, newOperationId());
      if (!result.taskId) throw new Error('The request could not be linked to a task.');

      return result.taskId;
    },
    [currentUser]
  );

  return {
    requests,
    loading,
    error,
    approveRequest,
    rejectRequest,
  };
}
